// Single source of truth for organizer-level pilgrim/dispute stat calculations.
//
// Why this exists: the codebase had 4 different inline implementations
// (OrganizersOverviewPage, LinkingStats, LinkingComparison, useStats.ts) that
// all computed "approved/pending/disputed" with subtly different rules:
//   - some counted links, some counted distinct passports
//   - some filtered by guideId (broken for waiting_* placeholder links),
//     some by organizerId
//   - some derived "disputed" from link.status, some from the Disputes
//     collection (the only stable source)
//   - useStats.ts mis-named "not owner" as `pendingLinks`
//
// Numbers between pages contradicted each other and made it impossible to tell
// which page was telling the truth. This module fixes that — every consumer
// must call computeOrgStats(...) and pick the field they want to display.

export type LinkLike = {
  organizerId?: string;
  guideId?: string;
  pilgrimId?: string;
  passportNumber?: string;
  status?: string;
  isOwner?: boolean | null;
  disputeId?: string | null;
};

export type PilgrimLike = { Passport_Number?: string };

export type DisputeLike = {
  passportNumber?: string;
  status?: string;
  disputingGuides?: Array<{ organizerId?: string; guideId?: string }>;
};

export type OrgPilgrimStats = {
  // ─── PASSPORT-level (deduped per passport) ───
  /** Distinct passports the org has any link to AND that exist in Pilgrims. */
  inBooking: number;
  /** Subset of inBooking where the org has at least one APPROVED-status link. */
  inBookingApproved: number;
  /** Distinct passports where the org's link is pending AND has NO approved
   *  companion. Matches "Pending tab" in an organizer's own view. */
  pendingOnly: number;
  /** Distinct passports in an active dispute naming this org, sourced from
   *  the Disputes collection (NOT link.status, which can lag/be stale). */
  disputed: number;

  // ─── owner/not-owner — derived from any-status links per passport ───
  /** Passports in inBooking where any link has isOwner !== false. */
  ownerCount: number;
  /** Passports in inBooking where every link has isOwner === false. */
  notOwnerCount: number;

  // ─── LINK-level (raw, no dedupe) — for compatibility with old views ───
  totalLinks: number;
  approvedLinks: number;
  pendingLinks: number;
  disputedLinks: number;
  waitingLinks: number;
};

export function emptyOrgStats(): OrgPilgrimStats {
  return {
    inBooking: 0, inBookingApproved: 0, pendingOnly: 0, disputed: 0,
    ownerCount: 0, notOwnerCount: 0,
    totalLinks: 0, approvedLinks: 0, pendingLinks: 0, disputedLinks: 0, waitingLinks: 0,
  };
}

/**
 * Compute every standard "stats card" number for one organizer in one pass.
 *
 * @param organizerId The Firestore doc id of the organizer.
 * @param links       Full PilgrimGuideLinks map (from usePilgrimGuideStore).
 *                    Will be filtered to this org via link.organizerId.
 * @param pilgrims    Full Pilgrims map (from usePilgrimsStore).
 * @param disputes    Full Disputes map (from useDisputesStore). Active states
 *                    are pending + under_processing.
 */
export function computeOrgStats(
  organizerId: string,
  links: Record<string, LinkLike> | LinkLike[] | undefined,
  pilgrims: Record<string, PilgrimLike> | PilgrimLike[] | undefined,
  disputes: Record<string, DisputeLike> | DisputeLike[] | undefined,
): OrgPilgrimStats {
  const out = emptyOrgStats();
  if (!organizerId) return out;

  const pilgrimList = Array.isArray(pilgrims) ? pilgrims : Object.values(pilgrims ?? {});
  const linkList    = Array.isArray(links)    ? links    : Object.values(links    ?? {});
  const disputeList = Array.isArray(disputes) ? disputes : Object.values(disputes ?? {});

  // 1. passport → pilgrim doc lookup. Lookup-by-passport (NOT pilgrimId) is
  // critical: waiting_* links have a synthetic pilgrimId that never matches a
  // real Pilgrim doc.
  const pilgrimsByPassport = new Map<string, PilgrimLike>();
  for (const p of pilgrimList) {
    const pp = String(p?.Passport_Number ?? '');
    if (pp) pilgrimsByPassport.set(pp, p);
  }

  // 2. org's links, grouped by passport (dedupe key).
  const linksByPassport = new Map<string, LinkLike[]>();
  for (const l of linkList) {
    if (l?.organizerId !== organizerId) continue;
    out.totalLinks++;
    switch (l.status) {
      case 'approved':                out.approvedLinks++;  break;
      case 'pending':                 out.pendingLinks++;   break;
      case 'disputed':                out.disputedLinks++;  break;
      case 'waiting_identification':  out.waitingLinks++;   break;
    }
    const pp = l.passportNumber;
    if (!pp) continue;
    const arr = linksByPassport.get(pp) ?? [];
    arr.push(l);
    linksByPassport.set(pp, arr);
  }

  // 3. per-passport rollups (the deduped numbers).
  for (const [passport, group] of linksByPassport) {
    if (!pilgrimsByPassport.has(passport)) continue;
    out.inBooking++;

    const hasApproved = group.some((l) => l.status === 'approved');
    if (hasApproved) {
      out.inBookingApproved++;
    } else if (group.some((l) => l.status === 'pending')) {
      out.pendingOnly++;
    }

    const isOwner = group.some((l) => l.isOwner !== false);
    if (isOwner) out.ownerCount++;
    else         out.notOwnerCount++;
  }

  // 4. disputed — Disputes collection is the source of truth, not link.status.
  const disputedPassports = new Set<string>();
  for (const d of disputeList) {
    if (d?.status !== 'pending' && d?.status !== 'under_processing') continue;
    if (!d.passportNumber) continue;
    if ((d.disputingGuides ?? []).some((g) => g?.organizerId === organizerId)) {
      disputedPassports.add(d.passportNumber);
    }
  }
  out.disputed = disputedPassports.size;

  return out;
}
